import EventEmitter2 from 'eventemitter2'

import { Signaling, SignalingStatus } from './Signaling'

export interface WebRTCOptions {
  serverUrl: string
}

export interface WebRTCClient {
  clientId: string | null
  peerConnection: RTCPeerConnection
  dataChannel: RTCDataChannel | null
  offer: RTCSessionDescription | null
  answer: RTCSessionDescription | null
  metadata: Record<string, any> | null
}

export enum WebRTCRole {
  UNKNOWN = 'unknown',
  HOST = 'host',
  CLIENT = 'client'
}

export enum WebRTCStatus {
  IDLE = 'idle',
  LOBBY = 'lobby',
  PLAYING = 'playing'
}

export interface PriorityInfo {
  clientId: string
  metadata: {}
}

export class WebRTC extends EventEmitter2 {
  public get signaling() {
    return this.internalSignaling!
  }

  public get role() {
    return this.internalRole
  }

  public get status() {
    return this.internalStatus
  }

  public get host() {
    return this.internalHost
  }

  public get clients() {
    return this.internalClients
  }

  public get meshClients() {
    return this.internalMeshClients
  }

  public get priorityClients() {
    return this.internalPriorityClients
  }

  public get myMetadata() {
    return this.internalMyMetadata
  }

  public get myClientId() {
    return this.internalSignaling?.clientId
  }

  private options: WebRTCOptions
  private internalSignaling: Signaling | null
  private internalMyMetadata: Record<string, any> | null = null

  private internalClients: Record<string, WebRTCClient> = {}
  private internalHost: WebRTCClient | null = null
  private internalMeshClients: Record<string, WebRTCClient> = {}
  private internalPriorityClients: PriorityInfo[] = []
  private newHostData: { clientId: string; newPriorityClients: PriorityInfo[] } | null = null
  private newHostUpdated = false

  private internalRole: WebRTCRole = WebRTCRole.UNKNOWN
  private internalStatus: WebRTCStatus = WebRTCStatus.IDLE

  public constructor(options: WebRTCOptions) {
    super({ wildcard: true })
    this.options = options
    this.internalSignaling = new Signaling({ serverUrl: this.options.serverUrl })
  }

  public async createHostSession(metadata: Record<string, any>) {
    if (!this.internalSignaling) throw new Error('Signaling not initialized')
    this.internalMyMetadata = metadata || null
    this.internalRole = WebRTCRole.HOST
    this.internalStatus = WebRTCStatus.LOBBY

    this.internalSignaling.on('offer-request', async (event) => {
      await this.handleOfferRequest(event.clientId, event.metadata)
    })

    this.internalSignaling.on('answer', async (event) => {
      await this.handleAnswer(event.answer, event.clientId)
    })

    this.internalSignaling.on('ice-candidate', async (event) => {
      await this.handleIceCandidate(event.candidate, event.clientId)
    })

    await this.internalSignaling.createSession()
  }

  public async joinSession(sessionId: string, metadata: Record<string, any>) {
    if (!this.internalSignaling) throw new Error('Signaling not initialized')
    this.internalRole = WebRTCRole.CLIENT
    this.internalStatus = WebRTCStatus.LOBBY
    this.internalMyMetadata = metadata || null

    this.internalSignaling.on('offer', async (event) => {
      await this.handleOffer(event.offer, event.metadata)
    })

    this.internalSignaling.on('ice-candidate', async (event) => {
      await this.handleIceCandidate(event.candidate)
    })

    this.internalSignaling.on('promoted-to-host', async (event) => {
      await this.handlePromotedToHost(event.sessionId)
    })

    this.internalSignaling.on('host-changed', async (event) => {
      await this.handleHostChanged(event.sessionId, event.newHostId)
    })

    // Listen for WebSocket disconnection to change status to PLAYING
    this.internalSignaling.on('websocket-closed', () => {
      this.internalStatus = WebRTCStatus.PLAYING
      this.emit('signaling-session-completed', {
        eventName: 'signaling-session-completed',
        status: this.internalStatus
      })
    })

    await this.internalSignaling.joinSession(sessionId, metadata)
  }

  public completeSession() {
    if (!this.internalSignaling) throw new Error('Signaling not initialized')

    this.internalSignaling.completeSession()

    this.startMeshConnections()

    this.internalStatus = WebRTCStatus.PLAYING
    this.emit('signaling-session-completed', { eventName: 'signaling-session-completed', status: this.internalStatus })
  }

  public sendMessageToClients(message: any) {
    for (const clientId in this.internalClients) {
      const client = this.internalClients[clientId]
      const stringifiedMessage = JSON.stringify({
        type: 'message',
        message: message
      })

      if (client) {
        client.dataChannel?.send(stringifiedMessage)
      }
    }
  }

  public sendMessageToHost(message: any) {
    const stringifiedMessage = JSON.stringify({
      type: 'message',
      message: message
    })

    this.internalHost?.dataChannel?.send(stringifiedMessage)
  }

  public updateMetadata(metadata: Record<string, any>) {
    if (this.internalRole === WebRTCRole.HOST) {
      // Update host's own metadata
      this.internalMyMetadata = metadata

      // Send metadata to all clients
      for (const clientId in this.internalClients) {
        const client = this.internalClients[clientId]
        if (client && client.dataChannel?.readyState === 'open') {
          client.dataChannel.send(
            JSON.stringify({
              type: 'metadata-update',
              clientId: null, // null indicates this is the host's metadata
              metadata: metadata
            })
          )
        }
      }

      this.emit('metadata-updated', { eventName: 'metadata-updated', metadata, role: this.internalRole })
    } else if (this.internalRole === WebRTCRole.CLIENT) {
      // Update client's own metadata
      this.internalMyMetadata = metadata

      // Send metadata to the host
      if (this.internalHost?.dataChannel?.readyState === 'open') {
        this.internalHost.dataChannel.send(
          JSON.stringify({
            type: 'metadata-update',
            clientId: this.internalSignaling?.clientId,
            metadata: metadata
          })
        )
      }

      // Send metadata to all mesh clients
      for (const meshClientId in this.internalMeshClients) {
        const meshClient = this.internalMeshClients[meshClientId]
        if (meshClient && meshClient.dataChannel?.readyState === 'open') {
          meshClient.dataChannel.send(
            JSON.stringify({
              type: 'metadata-update',
              clientId: this.internalSignaling?.clientId,
              metadata: metadata
            })
          )
        }
      }

      this.emit('metadata-updated', { eventName: 'metadata-updated', metadata, role: this.internalRole })
    }
  }

  private async handleOfferRequest(clientId: string, metadata: Record<string, any>) {
    this.emit('initializing-client', { eventName: 'initializing-client', clientId, metadata })
    const client = await this.initializeClient(clientId, metadata)
    this.internalClients[clientId] = client
    this.emit('client-initialized', { eventName: 'client-initialized', client })

    this.signaling.sendOffer(client.offer, clientId, this.internalMyMetadata || undefined)
  }

  private async handleOffer(offer: any, hostMetadata: Record<string, any>) {
    this.emit('initializing-host', { eventName: 'initializing-host', offer, hostMetadata })
    this.internalHost = await this.initializeHost(offer, hostMetadata, (channel) => {
      this.internalHost!.dataChannel = channel
    })
    this.emit('host-initialized', { eventName: 'host-initialized', host: this.internalHost })

    if (this.internalHost) this.signaling.sendAnswer(this.internalHost.answer)
  }

  private async handleAnswer(answer: any, clientId: string) {
    this.emit('completing-client', { eventName: 'completing-client', clientId })
    const client = this.internalClients[clientId]
    if (client) {
      await client.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))
      this.emit('client-completed', { eventName: 'client-completed', clientId, answer })
    }
  }

  private async handleIceCandidate(candidate: RTCIceCandidateInit, clientId?: string) {
    if (clientId) {
      // Host receiving ICE candidate from client
      const client = this.internalClients[clientId]
      if (client) {
        try {
          await client.peerConnection.addIceCandidate(new RTCIceCandidate(candidate))
          this.emit('ice-candidate-added', { eventName: 'ice-candidate-added', clientId, candidate })
        } catch (error) {
          this.emit('ice-candidate-error', { eventName: 'ice-candidate-error', clientId, error, candidate })
        }
      }
    } else {
      // Client receiving ICE candidate from host
      if (this.internalHost) {
        try {
          await this.internalHost.peerConnection.addIceCandidate(new RTCIceCandidate(candidate))
          this.emit('host-ice-candidate-added', { eventName: 'host-ice-candidate-added', candidate })
        } catch (error) {
          this.emit('host-ice-candidate-error', { eventName: 'host-ice-candidate-error', error, candidate })
        }
      }
    }
  }

  private async initializeClient(clientId: string, metadata: Record<string, any>) {
    let peerConnection: RTCPeerConnection | null = null
    let dataChannel: RTCDataChannel | null = null

    peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }]
    })

    peerConnection.onconnectionstatechange = () => {
      this.emit('connection-state-change', { eventName: 'connection-state-change', clientId, connectionState: peerConnection?.connectionState })

      // Handle disconnection
      if (peerConnection?.connectionState === 'disconnected' || peerConnection?.connectionState === 'failed' || peerConnection?.connectionState === 'closed') {
        this.handleClientDisconnection(clientId)
      }

      if (peerConnection?.connectionState === 'connected') {
        this.internalPriorityClients.push({ clientId, metadata })
        this.emit('broadcasting-priority-clients', { eventName: 'broadcasting-priority-clients', clients: this.internalPriorityClients })
        this.broadcastPriorityClients()
      }
    }

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.signaling.sendIceCandidate(event.candidate, clientId)
        this.emit('ice-candidate-sent', { eventName: 'ice-candidate-sent', clientId, candidate: event.candidate })
      } else {
        this.emit('ice-candidate-done', { eventName: 'ice-candidate-done', clientId })
      }
    }

    dataChannel = peerConnection.createDataChannel('messages')

    dataChannel.onopen = () => {
      this.emit('data-channel-open', { eventName: 'data-channel-open', clientId })
    }

    dataChannel.onclose = () => {
      this.emit('data-channel-close', { eventName: 'data-channel-close', clientId })
    }

    dataChannel.onmessage = async (event) => {
      const message = JSON.parse(event.data)

      switch (message.type) {
        case 'message':
          this.emit('client-message', { eventName: 'client-message', clientId, message: message.message })
          break
        case 'mesh-offer-relay':
          this.emit('mesh-offer-relay', {
            eventName: 'mesh-offer-relay',
            offer: message.offer,
            clientId: message.clientId,
            offererClientId: message.offererClientId,
            offererMetadata: message.offererMetadata
          })
          this.handleHostRelayMeshOffer(message.offer, message.clientId, message.offererClientId, message.offererMetadata)
          break
        case 'mesh-ice-candidate':
          this.emit('mesh-ice-candidate', { eventName: 'mesh-ice-candidate', candidate: message.candidate, clientId: message.clientId, offererClientId: message.offererClientId })
          this.handleHostRelayMeshIceCandidate(message.candidate, message.clientId, message.offererClientId)
          break
        case 'mesh-answer':
          this.emit('mesh-answer', { eventName: 'mesh-answer', answer: message.answer, clientId: message.clientId, answererClientId: message.answererClientId })
          this.handleHostRelayMeshAnswer(message.answer, message.clientId, message.answererClientId)
          break
        case 'metadata-update':
          this.emit('client-metadata-update', { eventName: 'client-metadata-update', clientId, metadata: message.metadata })
          this.handleClientMetadataUpdate(clientId, message.metadata)
          break
      }
    }

    this.emit('creating-offer', { eventName: 'creating-offer', clientId })

    const offer = await peerConnection?.createOffer()
    await peerConnection?.setLocalDescription(offer)

    return {
      clientId,
      peerConnection: peerConnection!,
      dataChannel,
      offer: peerConnection!.localDescription!,
      answer: null,
      metadata: metadata || null
    }
  }

  private async initializeHost(offer: any, metadata: Record<string, any>, setDataChannel: (channel: RTCDataChannel) => void) {
    const peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }]
    })

    peerConnection.onconnectionstatechange = () => {
      this.emit('host-connection-state-change', { eventName: 'host-connection-state-change', connectionState: peerConnection.connectionState })

      if (peerConnection.connectionState === 'disconnected' || peerConnection.connectionState === 'failed' || peerConnection.connectionState === 'closed') {
        this.handleHostDisconnection()
      }
    }

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.signaling.sendIceCandidate(event.candidate)
        this.emit('host-ice-candidate-sent', { eventName: 'host-ice-candidate-sent', candidate: event.candidate })
      } else {
        this.emit('host-ice-candidate-done', { eventName: 'host-ice-candidate-done' })
      }
    }

    peerConnection.ondatachannel = (event) => {
      setDataChannel(event.channel)

      event.channel.onopen = () => {
        this.emit('host-data-channel-open', { eventName: 'host-data-channel-open' })
      }

      event.channel.onclose = () => {
        this.emit('host-data-channel-close', { eventName: 'host-data-channel-close' })
      }

      event.channel.onmessage = async (event) => {
        const message = JSON.parse(event.data)

        switch (message.type) {
          case 'message':
            this.emit('host-message', { eventName: 'host-message', message: message.message })
            break
          case 'mesh-offer-request':
            this.emit('mesh-offer-request', { eventName: 'mesh-offer-request', clientId: message.clientId, metadata: message.metadata })
            this.handleMeshOfferRequest(message.clientId, message.metadata)
            break
          case 'mesh-offer':
            this.emit('mesh-offer', {
              eventName: 'mesh-offer',
              offer: message.offer,
              clientId: message.clientId,
              metadata: message.metadata
            })
            this.handleMeshOffer(message.clientId, message.offer, message.metadata)
            break
          case 'mesh-ice-candidate':
            this.emit('mesh-ice-candidate', { eventName: 'mesh-ice-candidate', candidate: message.candidate, clientId: message.clientId })
            this.handleMeshIceCandidate(message.candidate, message.clientId)
            break
          case 'mesh-answer':
            this.emit('mesh-answer', { eventName: 'mesh-answer', answer: message.answer, clientId: message.clientId })
            this.handleMeshAnswer(message.clientId, message.answer)
            break
          case 'priority-clients':
            this.emit('priority-clients', { eventName: 'priority-clients', clients: message.clients })
            this.handleHostPriorityClients(message.clients)
            break
          case 'metadata-update':
            this.emit('host-metadata-update', { eventName: 'host-metadata-update', clientId: message.clientId, metadata: message.metadata })
            this.handleHostMetadataUpdate(message.clientId, message.metadata)
            break
        }
      }
    }

    await peerConnection.setRemoteDescription(new RTCSessionDescription(offer))

    const answer = await peerConnection.createAnswer()
    await peerConnection.setLocalDescription(answer)

    return {
      clientId: null,
      dataChannel: null,
      peerConnection,
      answer: peerConnection.localDescription,
      offer,
      metadata: metadata || null
    }
  }

  private async handlePromotedToHost(sessionId: string) {
    this.emit('promoting-to-host', { eventName: 'promoting-to-host', sessionId })

    // Clear the current host since we're now the host
    this.internalHost?.peerConnection.close()
    this.internalHost = null
    this.internalPriorityClients = []

    // Set role to HOST since we're now the host
    this.internalRole = WebRTCRole.HOST

    // Update event listeners to handle host events
    this.internalSignaling?.removeAllListeners('event')

    // Set up host event listeners
    this.internalSignaling!.on('offer-request', async (event) => {
      await this.handleOfferRequest(event.clientId, event.metadata)
    })

    this.internalSignaling!.on('answer', async (event) => {
      await this.handleAnswer(event.answer, event.clientId)
    })

    this.internalSignaling!.on('ice-candidate', async (event) => {
      await this.handleIceCandidate(event.candidate, event.clientId)
    })

    this.emit('promoted-to-host', { eventName: 'promoted-to-host', sessionId })
  }

  private async handleHostChanged(sessionId: string, newHostId: string) {
    // If we're not the new host, we need to start fresh with the new host
    if (this.internalSignaling?.clientId !== newHostId) {
      // Clear all host references since we need to start over with the new host
      this.internalHost?.peerConnection.close()
      this.internalHost = null

      // Emit event to notify application that we need to restart connection
      this.emit('host-changed', { eventName: 'host-changed', sessionId, newHostId })
    }
  }

  private startMeshConnections() {
    const clients = Object.values(this.internalClients)

    for (let i = 0; i < clients.length; i++) {
      const client = clients[i]

      for (let j = i + 1; j < clients.length; j++) {
        const otherClient = clients[j]
        if (client && otherClient) {
          client.dataChannel?.send(
            JSON.stringify({
              type: 'mesh-offer-request',
              clientId: otherClient.clientId,
              metadata: otherClient.metadata
            })
          )
          this.emit('mesh-offer-request-sent', { eventName: 'mesh-offer-request-sent', clientId: client.clientId, otherClientId: otherClient.clientId })
        }
      }
    }
  }

  private async handleMeshOfferRequest(clientId: string, metadata: Record<string, any>) {
    this.emit('initializing-mesh-client', { eventName: 'initializing-mesh-client', clientId, metadata })
    const client = await this.initializeMeshClient(clientId, metadata)
    this.internalMeshClients[clientId] = client
    this.emit('mesh-client-initialized', { eventName: 'mesh-client-initialized', client })

    this.internalHost!.dataChannel!.send(
      JSON.stringify({ type: 'mesh-offer-relay', offer: client.offer, offererClientId: this.internalSignaling?.clientId, offererMetadata: this.internalMyMetadata, clientId })
    )
  }

  private async initializeMeshClient(clientId: string, metadata: Record<string, any>) {
    let peerConnection: RTCPeerConnection | null = null
    let dataChannel: RTCDataChannel | null = null

    peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }]
    })

    peerConnection.onconnectionstatechange = () => {
      this.emit('mesh-connection-state-change', { eventName: 'mesh-connection-state-change', clientId, connectionState: peerConnection?.connectionState })

      // Handle mesh client disconnection
      if (peerConnection?.connectionState === 'disconnected' || peerConnection?.connectionState === 'failed' || peerConnection?.connectionState === 'closed') {
        this.handleMeshClientDisconnection(clientId)
      }
    }

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.internalHost?.dataChannel?.send(
          JSON.stringify({ type: 'mesh-ice-candidate', candidate: event.candidate, clientId, offererClientId: this.internalSignaling!.clientId })
        )
        this.emit('mesh-ice-candidate-sent', { eventName: 'mesh-ice-candidate-sent', clientId, candidate: event.candidate })
      } else {
        this.emit('mesh-ice-candidate-done', { eventName: 'mesh-ice-candidate-done', clientId })
      }
    }

    dataChannel = peerConnection.createDataChannel('messages')

    dataChannel.onopen = () => {
      this.emit('mesh-data-channel-open', { eventName: 'mesh-data-channel-open', clientId })
    }

    dataChannel.onclose = () => {
      this.emit('mesh-data-channel-close', { eventName: 'mesh-data-channel-close', clientId })
    }

    dataChannel.onmessage = async (event) => {
      const message = JSON.parse(event.data)

      switch (message.type) {
        case 'message':
          this.emit('mesh-client-message', { eventName: 'mesh-client-message', clientId, message: message.message })
          break
        case 'new-host':
          this.emit('new-host', { eventName: 'new-host', clientId: message.clientId, newPriorityClients: message.newPriorityClients })
          this.newHostData = { clientId: message.clientId, newPriorityClients: message.newPriorityClients }

          if (!this.internalHost) this.handleNewHost()

          break
        case 'metadata-update':
          this.emit('mesh-client-metadata-update', { eventName: 'mesh-client-metadata-update', clientId: message.clientId, metadata: message.metadata })
          this.handleMeshClientMetadataUpdate(message.clientId, message.metadata)
          break
      }
    }

    this.emit('creating-mesh-offer', { eventName: 'creating-mesh-offer', clientId })

    const offer = await peerConnection?.createOffer()
    await peerConnection?.setLocalDescription(offer)

    return {
      clientId,
      peerConnection: peerConnection!,
      dataChannel,
      offer: peerConnection!.localDescription!,
      answer: null,
      metadata: metadata || null
    }
  }

  private async handleHostRelayMeshOffer(offer: any, clientId: string, offererClientId: string, offererMetadata: Record<string, any>) {
    this.emit('host-relaying-mesh-offer', { eventName: 'host-relaying-mesh-offer', offer, clientId, offererClientId, offererMetadata })
    const client = this.internalClients[clientId]
    if (client) {
      client.dataChannel?.send(JSON.stringify({ type: 'mesh-offer', offer, clientId: offererClientId, metadata: offererMetadata }))
      this.emit('host-relayed-mesh-offer', { eventName: 'host-relayed-mesh-offer', offer, clientId, offererClientId, offererMetadata })
    } else {
      this.emit('host-relayed-mesh-client-not-found', { eventName: 'host-relayed-mesh-client-not-found', offer, clientId, offererClientId, offererMetadata })
    }
  }

  private async handleMeshOffer(clientId: string, offer: any, metadata: Record<string, any>) {
    this.emit('initializing-mesh-offer', { eventName: 'initializing-mesh-offer', clientId, offer, metadata })
    const client = await this.initializeMeshOffer(clientId, offer, metadata, (channel) => {
      this.internalMeshClients[clientId].dataChannel = channel
    })
    this.internalMeshClients[clientId] = client
    this.emit('mesh-offer-initialized', { eventName: 'mesh-offer-initialized', client })

    if (this.internalHost) {
      this.internalHost.dataChannel!.send(JSON.stringify({ type: 'mesh-answer', answer: client.answer, clientId, answererClientId: this.internalSignaling!.clientId }))
    } else {
      this.emit('host-not-found', { eventName: 'host-not-found', clientId })
    }
  }

  private async initializeMeshOffer(clientId: string, offer: any, metadata: Record<string, any>, setDataChannel: (channel: RTCDataChannel) => void) {
    const peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }]
    })

    peerConnection.onconnectionstatechange = () => {
      this.emit('mesh-offer-connection-state-change', { eventName: 'mesh-offer-connection-state-change', connectionState: peerConnection.connectionState })

      // Handle mesh offer disconnection
      if (peerConnection.connectionState === 'disconnected' || peerConnection.connectionState === 'failed' || peerConnection.connectionState === 'closed') {
        this.handleMeshClientDisconnection(clientId)
      }
    }

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.internalHost?.dataChannel?.send(
          JSON.stringify({ type: 'mesh-ice-candidate', candidate: event.candidate, clientId: clientId, offererClientId: this.internalSignaling!.clientId })
        )
        this.emit('mesh-offer-ice-candidate-sent', { eventName: 'mesh-offer-ice-candidate-sent', candidate: event.candidate, clientId })
      } else {
        this.emit('mesh-offer-ice-candidate-done', { eventName: 'mesh-offer-ice-candidate-done', clientId })
      }
    }

    peerConnection.ondatachannel = (event) => {
      setDataChannel(event.channel)

      event.channel.onopen = () => {
        this.emit('mesh-offer-data-channel-open', { eventName: 'mesh-offer-data-channel-open' })
      }

      event.channel.onclose = () => {
        this.emit('mesh-offer-data-channel-close', { eventName: 'mesh-offer-data-channel-close' })
      }

      event.channel.onmessage = async (event) => {
        const message = JSON.parse(event.data)

        switch (message.type) {
          case 'message':
            this.emit('mesh-client-message', { eventName: 'mesh-client-message', message: message.message })
            break
          case 'mesh-offer-request':
            this.emit('mesh-offer-request', { eventName: 'mesh-offer-request', clientId: message.clientId, metadata: message.metadata })
            this.handleMeshOfferRequest(message.clientId, message.metadata)
            break
          case 'mesh-offer':
            this.emit('mesh-offer', { eventName: 'mesh-offer', offer: message.offer, clientId: message.clientId, metadata: message.metadata })
            this.handleMeshOffer(message.clientId, message.offer, message.metadata)
            break
          case 'new-host':
            this.emit('new-host', { eventName: 'new-host', clientId: message.clientId, newPriorityClients: message.newPriorityClients })
            this.newHostData = { clientId: message.clientId, newPriorityClients: message.newPriorityClients }

            if (!this.internalHost) this.handleNewHost()

            break
          case 'metadata-update':
            this.emit('mesh-client-metadata-update', { eventName: 'mesh-client-metadata-update', clientId: message.clientId, metadata: message.metadata })
            this.handleMeshClientMetadataUpdate(message.clientId, message.metadata)
            break
        }
      }
    }

    await peerConnection.setRemoteDescription(new RTCSessionDescription(offer))

    const answer = await peerConnection.createAnswer()
    await peerConnection.setLocalDescription(answer)

    return {
      clientId,
      dataChannel: null,
      peerConnection,
      answer: peerConnection.localDescription,
      offer,
      metadata: metadata || null
    }
  }

  private async handleHostRelayMeshIceCandidate(candidate: RTCIceCandidateInit, clientId: string, offererClientId: string) {
    this.emit('host-relaying-mesh-ice-candidate', { eventName: 'host-relaying-mesh-ice-candidate', candidate, clientId, offererClientId })
    const client = this.internalClients[clientId]
    if (client) {
      client.dataChannel?.send(JSON.stringify({ type: 'mesh-ice-candidate', candidate, clientId: offererClientId }))
      this.emit('host-relayed-mesh-ice-candidate', { eventName: 'host-relayed-mesh-ice-candidate', candidate, clientId, offererClientId })
    } else {
      this.emit('host-relayed-mesh-client-not-found', { eventName: 'host-relayed-mesh-client-not-found', candidate, clientId, offererClientId })
    }
  }

  private async handleMeshIceCandidate(candidate: RTCIceCandidateInit, clientId: string) {
    const client = this.internalMeshClients[clientId]
    if (client) {
      try {
        await client.peerConnection.addIceCandidate(new RTCIceCandidate(candidate))
        this.emit('mesh-ice-candidate-added', { eventName: 'mesh-ice-candidate-added', clientId, candidate })
      } catch (error) {
        this.emit('mesh-ice-candidate-error', { eventName: 'mesh-ice-candidate-error', clientId, error, candidate })
      }
    }
  }

  private async handleHostRelayMeshAnswer(answer: any, clientId: string, answererClientId: string) {
    this.emit('host-relaying-mesh-answer', { eventName: 'host-relaying-mesh-answer', answer, clientId })
    const client = this.internalClients[clientId]
    if (client) {
      client.dataChannel?.send(JSON.stringify({ type: 'mesh-answer', answer, clientId: answererClientId }))
      this.emit('host-relayed-mesh-answer', { eventName: 'host-relayed-mesh-answer', answer, clientId, answererClientId })
    } else {
      this.emit('host-relayed-mesh-client-not-found', { eventName: 'host-relayed-mesh-client-not-found', answer, clientId, answererClientId })
    }
  }

  private async handleMeshAnswer(clientId: string, answer: any) {
    this.emit('completing-mesh-client', { eventName: 'completing-mesh-client', clientId })
    const client = this.internalMeshClients[clientId]
    if (client) {
      await client.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))
      this.emit('mesh-client-completed', { eventName: 'mesh-client-completed', clientId, answer })
    }
  }

  private handleClientDisconnection(clientId: string) {
    const client = this.internalClients[clientId]
    if (client) {
      this.emit('client-disconnected', { eventName: 'client-disconnected', clientId })

      delete this.internalClients[clientId]

      // Remove from priority list
      this.internalPriorityClients = this.internalPriorityClients.filter((internalClient) => internalClient.clientId !== clientId)

      // Broadcast updated priority list to remaining clients
      this.broadcastPriorityClients()

      this.emit('client-removed', { eventName: 'client-removed', clientId })
    } else {
      this.emit('client-not-found', { eventName: 'client-not-found', clientId })
    }
  }

  private handleMeshClientDisconnection(clientId: string) {
    const meshClient = this.internalMeshClients[clientId]
    if (meshClient) {
      this.emit('mesh-client-disconnected', { eventName: 'mesh-client-disconnected', clientId })
      delete this.internalMeshClients[clientId]

      this.emit('mesh-client-removed', { eventName: 'mesh-client-removed', clientId })
    } else {
      this.emit('mesh-client-not-found', { eventName: 'mesh-client-not-found', clientId })
    }
  }

  private handleHostDisconnection() {
    // Signaling has its own host promotion so only if its close it means we can work with disconnections through WebRTC
    if (this.signaling.status === SignalingStatus.CLOSED) {
      this.emit('host-disconnected', { eventName: 'host-disconnected' })
      this.internalHost = null

      if (this.newHostData) {
        this.handleNewHost()
      } else if (!this.newHostUpdated) {
        if (this.internalPriorityClients[0].clientId === this.internalSignaling?.clientId) {
          this.handleSelfHost()
        }
      } else {
        this.newHostUpdated = false
      }
    }
  }

  private async broadcastPriorityClients() {
    for (let i = 0; i < this.internalPriorityClients.length; i++) {
      const clientId = this.internalPriorityClients[i].clientId
      const client = this.internalClients[clientId]
      if (client) {
        if (client.dataChannel?.readyState !== 'open') {
          await new Promise((resolve) => {
            const handleChannelOpen = () => {
              if (client.dataChannel?.readyState === 'open') {
                client.dataChannel?.removeEventListener('open', handleChannelOpen)
                resolve(true)
              }
            }
            client.dataChannel?.addEventListener('open', handleChannelOpen)
          })
        }

        client.dataChannel?.send(JSON.stringify({ type: 'priority-clients', clients: this.internalPriorityClients }))
      }
    }
    this.emit('broadcasted-priority-clients', { eventName: 'broadcasted-priority-clients', clients: this.internalPriorityClients })
  }

  private handleHostPriorityClients(clients: PriorityInfo[]) {
    this.internalPriorityClients = clients
    this.emit('host-priority-clients-updated', { eventName: 'host-priority-clients-updated', clients })
  }

  private handleSelfHost() {
    this.internalClients = this.internalMeshClients
    this.internalMeshClients = {}
    this.internalPriorityClients = this.internalPriorityClients.filter((client) => this.internalClients[client.clientId])
    this.newHostData = null

    // Set role to HOST since we're now the host
    this.internalRole = WebRTCRole.HOST

    for (let i = 0; i < this.internalPriorityClients.length; i++) {
      const clientId = this.internalPriorityClients[i].clientId
      const client = this.internalClients[clientId]
      if (client) {
        client.dataChannel?.send(JSON.stringify({ type: 'new-host', clientId: this.internalSignaling?.clientId, newPriorityClients: this.internalPriorityClients }))
      }
    }

    this.emit('self-promoted-to-host', { eventName: 'self-promoted-to-host', newPriorityClients: this.internalPriorityClients })
  }

  private handleNewHost() {
    this.emit('setting-new-host', { eventName: 'setting-new-host', clientId: this.newHostData?.clientId!, newPriorityClients: this.newHostData?.newPriorityClients! })
    this.internalHost = this.internalMeshClients[this.newHostData?.clientId!]
    delete this.internalMeshClients[this.newHostData?.clientId!]

    this.internalPriorityClients = this.newHostData?.newPriorityClients!

    this.newHostData = null
    this.newHostUpdated = true
  }

  private handleClientMetadataUpdate(clientId: string, metadata: Record<string, any>) {
    // Update the client's metadata
    const client = this.internalClients[clientId]
    if (client) {
      client.metadata = metadata

      // If signaling is still active, relay to all other clients
      if (this.internalStatus === WebRTCStatus.LOBBY && this.internalSignaling?.status !== SignalingStatus.CLOSED) {
        // Relay to all other clients (not the sender)
        for (const otherClientId in this.internalClients) {
          if (otherClientId !== clientId) {
            const otherClient = this.internalClients[otherClientId]
            if (otherClient && otherClient.dataChannel?.readyState === 'open') {
              otherClient.dataChannel.send(
                JSON.stringify({
                  type: 'metadata-update',
                  clientId: clientId,
                  metadata: metadata
                })
              )
            }
          }
        }
      }
      // If signaling is complete, client will handle sharing to mesh clients themselves

      this.emit('client-metadata-updated', { eventName: 'client-metadata-updated', clientId, metadata })
    }
  }

  private handleHostMetadataUpdate(clientId: string | null, metadata: Record<string, any>) {
    // If clientId is null, it's the host's metadata
    if (clientId === null) {
      this.internalHost!.metadata = metadata
      this.emit('host-metadata-received', { eventName: 'host-metadata-received', metadata })
    } else {
      // It's another client's metadata being relayed through the host
      const meshClient = this.internalMeshClients[clientId]
      if (meshClient) {
        meshClient.metadata = metadata
        this.emit('peer-metadata-updated', { eventName: 'peer-metadata-updated', clientId, metadata })
      }
    }
  }

  private handleMeshClientMetadataUpdate(clientId: string, metadata: Record<string, any>) {
    // Update the mesh client's metadata
    const meshClient = this.internalMeshClients[clientId]
    if (meshClient) {
      meshClient.metadata = metadata
      this.emit('peer-metadata-updated', { eventName: 'peer-metadata-updated', clientId, metadata })
    }
  }
}
